import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";

interface TorchBackgroundProps {
  className?: string;
  /** Override torch positions (percent x/y). Defaults to four wall-sconce positions. */
  positions?: { x: number; y: number }[];
}

interface Torch {
  id: number;
  x: number;
  y: number;
  size: number;
  flickerSpeed: number;
  flickerDelay: number;
  glowSize: number;
  depthScale: number;
}

const DEFAULT_POSITIONS = [
  { x: 12, y: 38 },
  { x: 26, y: 46 },
  { x: 74, y: 46 },
  { x: 88, y: 38 },
  { x: 40, y: 52 },
  { x: 60, y: 52 },
];

const TorchBackground: React.FC<TorchBackgroundProps> = ({
  className = "",
  positions = DEFAULT_POSITIONS,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const dimRef = useRef<number[]>([]);
  const extinguishedRef = useRef<boolean[]>([]);
  const extinguishedAtRef = useRef<number[]>([]);
  const rafRef = useRef<number>();

  const torches = useMemo<Torch[]>(() => {
    const maxDist = Math.sqrt(50 * 50 + 50 * 50);
    const result = positions.map((p, i) => {
      const dist = Math.sqrt((p.x - 50) ** 2 + (p.y - 50) ** 2);
      const depthScale = 0.55 + (dist / maxDist) * 0.45;
      return {
        id: i,
        x: p.x,
        y: p.y,
        size: (22 + (i % 2) * 4) * depthScale,
        flickerSpeed: 1.6 + Math.random() * 1.6,
        flickerDelay: -(Math.random() * 4),
        glowSize: (220 + Math.random() * 60) * depthScale,
        depthScale,
      };
    });
    dimRef.current = result.map(() => 0);
    extinguishedRef.current = result.map(() => false);
    extinguishedAtRef.current = result.map(() => 0);
    return result;
  }, [positions]);

  const [dimFactors, setDimFactors] = useState<number[]>(() => torches.map(() => 0));
  const [extinguished, setExtinguished] = useState<boolean[]>(() => torches.map(() => false));

  const animate = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }
    const rect = container.getBoundingClientRect();
    const mouse = mouseRef.current;
    const influence = 110; // tighter — needs deliberate hover
    const riseRate = 0.35;

    let changed = false;
    let latchChanged = false;
    torches.forEach((torch, i) => {
      // Once extinguished, stay extinguished — no relight.
      if (extinguishedRef.current[i]) {
        if (dimRef.current[i] !== 1) {
          dimRef.current[i] = 1;
          changed = true;
        }
        // Keep re-rendering while smoke is fading so opacity recomputes.
        const elapsed = performance.now() - (extinguishedAtRef.current[i] || 0);
        if (elapsed < 2000) changed = true;
        return;
      }
      const cx = (torch.x / 100) * rect.width;
      const cy = (torch.y / 100) * rect.height;
      let target = 0;
      if (mouse.active) {
        const dx = cx - (mouse.x - rect.left);
        const dy = cy - (mouse.y - rect.top);
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < influence) target = 1; // fully extinguish on direct hover
      }
      const cur = dimRef.current[i];
      // Only allow dimming up toward extinguish; never relight on its own.
      const next = target > cur ? cur + (target - cur) * riseRate : cur;
      if (Math.abs(next - cur) > 0.002) changed = true;
      dimRef.current[i] = next;
      if (next >= 0.92 && !extinguishedRef.current[i]) {
        extinguishedRef.current[i] = true;
        extinguishedAtRef.current[i] = performance.now();
        latchChanged = true;
      }
    });
    if (changed) setDimFactors([...dimRef.current]);
    if (latchChanged) setExtinguished([...extinguishedRef.current]);
    rafRef.current = requestAnimationFrame(animate);
  }, [torches]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { ...mouseRef.current, active: false };
    };
    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div ref={containerRef} className={`overflow-hidden pointer-events-none ${className}`}>
      {torches.map((torch, i) => {
        const dim = dimFactors[i] ?? 0;
        const lit = 1 - dim;
        const isOut = extinguished[i];
        const elapsed = isOut ? performance.now() - (extinguishedAtRef.current[i] || 0) : 0;
        const fadeDuration = 2000; // ms to fully fade
        const fadeProgress = Math.min(elapsed / fadeDuration, 1);
        const smokeOpacity = isOut ? 1 - fadeProgress : dim > 0.5 ? (dim - 0.5) * 2 : 0;
        const smokeActive = smokeOpacity > 0;
        return (
          <div
            key={torch.id}
            className="absolute"
            style={{ left: `${torch.x}%`, top: `${torch.y}%` }}
          >
            {/* Sconce bracket (always visible) */}
            <div
              className="absolute"
              style={{
                width: 14,
                height: 4,
                left: -7,
                top: 4,
                background: "linear-gradient(to bottom, #2a1d12, #110a05)",
                borderRadius: 1,
                boxShadow: "0 1px 0 rgba(0,0,0,0.6)",
              }}
            />
            {/* Warm ambient glow */}
            <div
              className="absolute rounded-full"
              style={{
                width: torch.glowSize,
                height: torch.glowSize,
                left: -torch.glowSize / 2,
                top: -torch.glowSize / 2,
                background:
                  "radial-gradient(circle, rgba(255,140,40,0.28) 0%, rgba(200,80,15,0.10) 40%, transparent 70%)",
                opacity: lit,
                transition: "opacity 0.15s linear",
                animation: `torch-glow ${torch.flickerSpeed}s ease-in-out ${torch.flickerDelay}s infinite`,
              }}
            />
            {/* Flame core */}
            <div
              className="absolute"
              style={{
                width: torch.size,
                height: torch.size * 2.1,
                left: -torch.size / 2,
                top: -torch.size * 1.4,
                background:
                  "radial-gradient(ellipse at 50% 85%, rgba(255,230,120,0.95) 0%, rgba(255,150,40,0.75) 30%, rgba(255,70,10,0.4) 60%, transparent 82%)",
                borderRadius: "50% 50% 50% 50% / 65% 65% 35% 35%",
                filter: `blur(${torch.size * 0.18}px)`,
                opacity: lit,
                transformOrigin: "50% 100%",
                transform: `scaleY(${0.2 + lit * 0.8})`,
                transition: "opacity 0.15s linear, transform 0.2s ease-out",
                animation: `torch-flicker ${torch.flickerSpeed}s ease-in-out ${torch.flickerDelay}s infinite`,
              }}
            />
            {/* Inner bright core */}
            <div
              className="absolute"
              style={{
                width: torch.size * 0.45,
                height: torch.size * 0.9,
                left: -torch.size * 0.225,
                top: -torch.size * 0.85,
                background:
                  "radial-gradient(ellipse, rgba(255,255,210,0.85) 0%, rgba(255,200,90,0.45) 50%, transparent 80%)",
                borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
                filter: `blur(${torch.size * 0.1}px)`,
                opacity: lit,
                transition: "opacity 0.15s linear",
                animation: `torch-flicker ${torch.flickerSpeed * 0.7}s ease-in-out ${torch.flickerDelay - 0.3}s infinite`,
              }}
            />
            {/* Smoke wisp on extinguish */}
            <div
              className="absolute"
              style={{
                width: torch.size * 2,
                height: torch.size * 4,
                left: -torch.size,
                top: -torch.size * 2.4,
                background:
                  "radial-gradient(ellipse at 50% 90%, rgba(220,220,220,0.7) 0%, rgba(160,160,160,0.4) 35%, rgba(100,100,100,0.2) 60%, transparent 80%)",
                borderRadius: "50%",
                filter: "blur(4px)",
                opacity: smokeOpacity,
                animation: smokeActive
                  ? `torch-smoke ${2.2 + (i % 3) * 0.4}s ease-out ${-i * 0.3}s forwards`
                  : "none",
              }}
            />
          </div>
        );
      })}

      <style>{`
        @keyframes torch-flicker {
          0%, 100% { transform: scaleY(1) scaleX(1); }
          20% { transform: scaleY(1.1) scaleX(0.92) translateY(-1px); }
          40% { transform: scaleY(0.92) scaleX(1.06) rotate(1.5deg); }
          60% { transform: scaleY(1.06) scaleX(0.95) rotate(-1.5deg); }
          80% { transform: scaleY(0.95) scaleX(1.08) translateY(-2px); }
        }
        @keyframes torch-glow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes torch-smoke {
          0% { transform: translateY(0) scale(0.7); }
          100% { transform: translateY(-70px) scale(1.6); }
        }
      `}</style>
    </div>
  );
};

export default TorchBackground;